// @ts-nocheck
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { ContentWrapper } from "@insite/content-library/Widgets/Basic/Button";
import Button from "@insite/mobius/Button";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { widgetWrapperStyles } from "@store/Library/Components/Toolkit/WidgetWrapper";
import getBooleanProperty from "@toolkit/Framework/Common/Utilities/getBooleanProperty";
import * as cssLinter from "css";
import React from "react";
import { useContext } from "react";
import { useSelector } from "react-redux";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
        productState: getProductState(state, state.pages.productDetails.selectedProductId).value,
    };
};

type Props = WidgetProps & OwnProps & ReturnType<typeof mapStateToProps>;

const enum fields {
    variant = "variant",
    label = "label",
    alignment = "alignment",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.label]: string;
        [fields.alignment]: string;
        [fields.customCSS]: string;
    };
}

const wrapperStyles: InjectableCss = widgetWrapperStyles;

const SignInPricingButton = ({ fields, isSignedIn, productState }: Props) => {
    const history = useHistory();
    const state = useSelector((state: ApplicationState) => {
        return {
            currentLocation: getLocation(state),
            signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        };
    });
    const { product } = useContext(ProductContext);
    const isVariantParent = productState?.isVariantParent;

    const signInHandler = () => {
        if (!state.signInUrl) {
            logger.warn("No url was found for SignInPage, defaulting to /SignIn");
            history.push("/MyAccount/SignIn");
        } else {
            const returnUrl = state.currentLocation.pathname + state.currentLocation.search;
            if (returnUrl === "/") {
                history.push(`${state.signInUrl}`);
            } else {
                history.push(`${state.signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}`);
            }
        }
    };

    const styles = useMergeStyles("button", { wrapperStyles });

    const customCssWrapper = {
        css: css`
            ${styles?.wrapperStyles?.css}
            ${fields.customCSS}
        `,
    };

    const productProperties = product.properties;
    const canRequestQuote = getBooleanProperty(productProperties?.canRequestQuote);
    const hasCustomPrice = !!productProperties.customPrice || !!productState?.properties.customPrice;

    if (isVariantParent || (!hasCustomPrice && canRequestQuote)) {
        return null;
    }

    return (
        <>
            {!isSignedIn && (
                <ContentWrapper alignment={fields.alignment} {...customCssWrapper} className="wrapper">
                    <Button variant={fields.variant} onClick={signInHandler} className="button">
                        {productState?.canShowPrice ? translate("grimco_sign_in_to_add_to_cart") : fields.label}
                    </Button>
                </ContentWrapper>
            )}
        </>
    );
};

const defaultCustomCss = `& {
}

.button {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(SignInPricingButton),
    definition: {
        group: "Common",
        icon: "rectangle-wide",
        displayName: "Sign In for Pricing Button",
        allowedContexts: ["ProductDetailsPage", "ProductListPage", "CartPage"],
        fieldDefinitions: [
            {
                name: fields.variant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "Sign In For Pricing",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.alignment,
                displayName: "Button Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "primary",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
